/**
 * UCC Bulk Operations Hooks
 *
 * React Query hooks for bulk filing operations (continuations, amendments, imports).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { uccBulkOperationsClient } from '../api/typed-clients';
import { apiErrorMessage } from '../api/error-message';

const BULK_OPS_KEY = 'uccBulkOperations';

function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.error || !result.data) {
    const status = result.response?.status ?? 0;
    const statusText = result.response?.statusText ?? 'Unknown';
    const errorBody = result.error as { message?: string } | undefined;
    throw new Error(apiErrorMessage(errorBody) ?? `HTTP ${status}: ${statusText}`);
  }
  return result.data;
}

export function useBulkOperations(params: { tenantId: string; page?: number; limit?: number }) {
  return useQuery({
    queryKey: [BULK_OPS_KEY, params],
    queryFn: async () => {
      const result = await uccBulkOperationsClient.GET('/ucc/bulk-operations', {
        params: {
          query: {
            tenantId: params.tenantId,
            page: params.page,
            limit: params.limit,
          },
        },
      });
      return unwrap(result);
    },
    enabled: !!params.tenantId,
  });
}

export function useBulkOperation(batchId: string | undefined) {
  return useQuery({
    queryKey: [BULK_OPS_KEY, batchId],
    queryFn: async () => {
      const result = await uccBulkOperationsClient.GET('/ucc/bulk-operations/{batchId}', {
        params: { path: { batchId: batchId! } },
      });
      return unwrap(result);
    },
    enabled: !!batchId,
  });
}

export function useBulkOperationResults(batchId: string | undefined) {
  return useQuery({
    queryKey: [BULK_OPS_KEY, batchId, 'results'],
    queryFn: async () => {
      const result = await uccBulkOperationsClient.GET('/ucc/bulk-operations/{batchId}/results', {
        params: { path: { batchId: batchId! } },
      });
      return unwrap(result);
    },
    enabled: !!batchId,
  });
}

export function useCreateBulkOperation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: never) => {
      const result = await uccBulkOperationsClient.POST('/ucc/bulk-operations', { body });
      return unwrap(result);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BULK_OPS_KEY] });
    },
  });
}

export function useValidateBulkOperation() {
  return useMutation({
    mutationFn: async (body: never) => {
      const result = await uccBulkOperationsClient.POST('/ucc/bulk-operations/validate', { body });
      return unwrap(result);
    },
  });
}
